import {
  ContactInfoSchemaType,
  ControlledField,
  PhoneInputField,
  contactInfoDtoSchema,
} from 'remote/global';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { Stack } from '@mui/system';
import { useForm } from 'react-hook-form';
import { Button } from '@mui/material';
import { toast } from 'react-hot-toast';
import { useUsersContactInfoMutation } from 'api/queries/users';
import PaperLayout from '../../PaperLayout';

function AccountDetailsForm({ defaultValues }: { defaultValues?: ContactInfoSchemaType }) {
  const tInstance = useTranslation();
  const { t } = tInstance;
  const {
    handleSubmit,
    control,
    formState: { isSubmitting, isValidating, isLoading },
  } = useForm<ContactInfoSchemaType>({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      ...defaultValues,
    },
    resolver: zodResolver(contactInfoDtoSchema),
  });
  const contactInfoMutation = useUsersContactInfoMutation();

  const onValid = async (data: ContactInfoSchemaType) => {
    try {
      const response = await contactInfoMutation.mutateAsync(data);
      if (response != null) {
        toast.success(t('Toast.success'));
      }
    } catch (err) {
      toast.error(t('Toast.failure'));
    }
  };

  return (
    <PaperLayout
      title={t('SettingsView.account.title')}
      subtitle={t('SettingsView.account.subtitle')}
    >
      <form onSubmit={handleSubmit(onValid)}>
        <Stack direction='column' spacing={1}>
          <ControlledField
            control={control}
            name='firstName'
            fieldProps={{ InputProps: { autoComplete: 'given-name' } }}
          />
          <ControlledField
            control={control}
            name='lastName'
            fieldProps={{ InputProps: { autoComplete: 'family-name' } }}
          />
          <ControlledField
            control={control}
            name='email'
            translationKey='personalEmail'
            fieldProps={{
              helperText: '',
              InputProps: {
                type: 'email',
                autoComplete: 'email',
              },
            }}
          />
          <ControlledField
            control={control}
            name='phone'
            render={(renderProps) => <PhoneInputField {...renderProps} />}
          />
        </Stack>
        <Button
          type='submit'
          disabled={isSubmitting || isValidating || isLoading}
          variant='contained'
          fullWidth
          sx={{ mt: '2rem' }}
        >
          {t('save')}
        </Button>
      </form>
    </PaperLayout>
  );
}

export default AccountDetailsForm;
